import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { useParams } from 'react-router';
import ConfirmDialog from '@/components/UI/Dialog/ConfirmDialog';
import VehicleCleanDirt from '@/containers/CampaignDetail/AffectedVehicles/Volume/VolumeDetails/VehicleCleanDirt/VehicleCleanDirt';
import styles from '@/containers/CampaignDetail/AffectedVehicles/Volume/VolumeDetails/VolumeDetails.module.scss';
import VolumeTabs from '@/containers/CampaignDetail/AffectedVehicles/Volume/VolumeDetails/VolumeTabs/VolumeTabs';
import AttachmentCollection from '@/containers/CampaignDetail/Attachments/AttachmentCollection/AttachmentCollection';
import { volumeList, mapperModelSeries, allExtensionsFile, COMPLETED, ERROR } from '@/helpers/constants';
import { splitFileName, prepareFiles as prepareFilesUtil, removePercentageFromName } from '@/helpers/fileUtils';
import { limitOfVolumeTabsMapping } from '@/helpers/opensearch-utils';
import { reverseObjectKeysAndValues } from '@/helpers/utils';
import useCheckDisabled from '@/hooks/useCheckDisabled';
import useDownloadFile from '@/hooks/useDownloadFile';
import useSave from '@/hooks/useSave';
import useToastContext from '@/hooks/useToastContext';
import {
  setCampaignVehicles,
  deletePlantAttachments,
  addPlantAttachments,
  copyAffectedAggregate,
  copyAffectedVehiclesModelDesignation,
  setAffectedVehiclesValidations,
} from '@/redux/Campaign/campaign.action';
import {
  getCampaignVehicles,
  getCampaignInfo,
  getCampaignVehiclesVolumes,
  getAffectedVehiclesValidations,
  getCampaignValidations,
  getLimitationPlants,
} from '@/redux/Campaign/campaign.reducer';
import { setVolumesForms } from '@/redux/Forms/forms.action';
import { getVolumesForms } from '@/redux/Forms/forms.reducer';
import { getHeaderLanguage } from '@/redux/Header/header.reducer';
import affectedVehicleService from '@/services/affectedVehicleService';
import campaignServices from '@/services/campaignServices';
import documentationServices from '@/services/documentationServices';
import CodeRule from './CodeRule/CodeRule';
import Summary from './Summary/Summary';
import { validateVehicleVolume } from './VehicleCleanDirt/VehicleCleanDirt-utils';
import { COLUMNS } from './volumeDetails-constants';
import {
  getVolumeSelected,
  getTabInputs,
  mapVehiclesModelDesignation,
  mapAggregateList,
  copyAggregate,
  copyModelDesignation,
} from './volumeDetails-utils';
import { isViaModelSeries } from '../../SeriesPanel/SeriesRow/series-row-utils';

const VolumeDetails = (props) => {
  const { isReadOnly, affectedVehicles, validations, series, volumes, affectedVehiclesValidations, headerLanguage,
    setCopyAggreagate, setCopyModelDesignation, deleteAttachmentsPlant, plantSelected, tabSelected, setTabSelected,
    addAttachmentsPlant, manualEntry, volumeForms, setForms, setPlantsValidations, limitationPlants } = props;
  const { id } = useParams();
  const { t } = useTranslation();
  const checkDisabled = useCheckDisabled(true);
  const { addToast } = useToastContext();
  const [enginesModelsList, setEnginesModelsList] = useState({});
  const [deleteFileId, setDeleteFileId] = useState(null);
  const volumeSelected = volumes?.find(({ plant }) => plant === plantSelected?.wkzNumber);
  const { downloadFile } = useDownloadFile();
  const saveChanges = useSave('affected-vehicles');
  const limitOfVolumeTabsMappingInverse = useMemo(() => reverseObjectKeysAndValues(limitOfVolumeTabsMapping), []);
  const [uploadProgress, setUploadProgress] = useState(null);

  const loadList = () => {
    Promise.all([
      affectedVehicleService.getCountryVariants(),
      affectedVehicleService.getPaintCodes(),
    ]).then((res) => {
      const [countries, paints] = res;
      setForms({
        COUNTRY_VARIANTS: countries,
        PAINT_CODE: paints,
      });
    }).catch((error) => addToast(error.message, 'ERROR'));
  };

  const loadEnginesModelsList = () => {
    if (plantSelected && isViaModelSeries(affectedVehicles) && ['AGGREGATE', 'VEHICLE_MODEL_DESIGNATION'].includes(tabSelected)) {
      const optionsSelected = getVolumeSelected(getTabInputs(tabSelected)[0].attr, volumes, plantSelected);
      const filteredSeries = series.filter((serie) => Object.values(serie.typeVersions).some(
        (tv) => tv.some(({ wkzNumber }) => wkzNumber === plantSelected?.wkzNumber),
      ));
      const body = mapperModelSeries(filteredSeries, plantSelected);

      if (Object.values(body).flat().length > 0) {
        Promise.all([
          affectedVehicleService.getVehiclesModelDesignation(body),
          affectedVehicleService.getEnginesModelDesignation(body),
        ]).then((res) => {
          const [models, engines] = res;
          setEnginesModelsList({
            AGGREGATE: mapAggregateList(JSON.parse(engines), headerLanguage),
            VEHICLE_MODEL_DESIGNATION: mapVehiclesModelDesignation(JSON.parse(models).vehicles, headerLanguage),
          });
          copyModelDesignation(optionsSelected, JSON.parse(models).vehicles, setCopyModelDesignation);
          copyAggregate(optionsSelected, JSON.parse(engines), setCopyAggreagate);
        }).catch((error) => addToast(error.message, 'ERROR'));
      }
    }
  };

  useEffect(() => {
    if (!(Object.keys(volumeForms || {}).length > 0)) {
      loadList();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    validateVehicleVolume(volumes, headerLanguage, validations, setPlantsValidations, plantSelected, limitationPlants);
  }, [volumeSelected?.vehicleRestrictionFiles?.length]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadEnginesModelsList();
  }, [series, tabSelected, plantSelected]);

  const getTabContent = () => {
    if (Object.keys(COLUMNS).includes(tabSelected)) {
      return (
        <VolumeTabs
          plantSelected={plantSelected}
          checkEnabled={checkDisabled}
          tabSelected={tabSelected}
          enginesModelsList={enginesModelsList}
          isReadOnly={isReadOnly}
        />
      );
    }

    if (tabSelected === 'SUMMARY') {
      return <Summary plantSelected={plantSelected} series={series} volumes={volumes} checkEnabled={checkDisabled} />;
    }

    if (tabSelected === 'CODE_RULE') {
      return <CodeRule plantSelected={plantSelected} volumes={volumes} />;
    }

    return <VehicleCleanDirt plantSelected={plantSelected} checkEnabled={checkDisabled} tabSelected={tabSelected} />;
  };

  const selectTab = (tab) => {
    setTabSelected(tab);
  };

  const getSelectTab = (tab) => (tabSelected === tab ? styles.TabSelected : '');

  const validateActive = () => (validations?.active?.all ? 'error ValidationIndicatorField' : '');

  const deleteFile = (file) => {
    campaignServices.deleteAttachmentPlant(file, id, plantSelected.wkzNumber)
      .then(() => {
        deleteAttachmentsPlant(plantSelected.wkzNumber, file);
      })
      .catch((error) => addToast(error.message, 'ERROR'));
    setDeleteFileId(null);
  };

  const handleTypeError = (name) => addToast(`${name}: ${t('alert.wrongFileType')}`, ERROR);
  const plantIndex = volumes?.findIndex((v) => v.plant === plantSelected?.wkzNumber) ?? -1;

  const prepareFiles = (files = []) => {
    const newAttachments = prepareFilesUtil(files);

    addAttachmentsPlant({ plant: plantSelected.wkzNumber, files: newAttachments });
  };

  const uploadFile = async (file, onProgress) => {
    const f = new FormData();
    f.append('file', file);
    const { extFile } = splitFileName(file.name);

    if (!allExtensionsFile.includes(extFile)) {
      handleTypeError(file.name);

      return false;
    }

    try {
      const res = await documentationServices.uploadAttachmentPlant(
        f,
        id,
        'affected-vehicles',
        file.name,
        plantSelected.wkzNumber,
        (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        },
      );
      const newAttachment = {
        date: new Date(),
        favorite: false,
        id: res,
        fullName: file.name,
        owner: id,
        status: COMPLETED,
      };

      return newAttachment;
    } catch (error) {
      addToast(error?.message, 'ERROR');

      return false;
    }
  };

  const uploadFiles = async (files) => {
    const updatedFiles = removePercentageFromName(files);

    prepareFiles(updatedFiles);
    const realFiles = [];

    await Promise.allSettled(Object.values(updatedFiles).map(async (file) => {
      const newFileUploaded = await uploadFile(file, (progress) => {
        setUploadProgress((prevState) => ({
          ...prevState,
          [file.name]: progress,
        }));
      });
      if (newFileUploaded) realFiles.push(newFileUploaded);
    })).then(async () => {
      addAttachmentsPlant({ plant: plantSelected.wkzNumber, files: realFiles });
      setUploadProgress(null);
      await saveChanges();
    });

    document.getElementById('addAttachment').value = '';
  };

  const tabInvalid = (volumeTab) => {
    const volumesValidations = affectedVehiclesValidations?.[headerLanguage]?.volumes;
    const validated = Object.keys(volumesValidations || {}).length > 0;
    const nokOkInvalid = volumeTab === 'CLEAN_DIRTY_POINT' && volumesValidations?.[plantSelected?.wkzNumber]?.vehiclesCleanDirt?.invalid;
    const volumeTabInvalid = volumesValidations?.[plantSelected?.wkzNumber]?.[limitOfVolumeTabsMappingInverse?.[volumeTab]]?.invalid;

    return validated && (nokOkInvalid || volumeTabInvalid);
  };

  return (
    <>
      {manualEntry && plantSelected
        ? (
          <div className={styles.Tabs}>
            <div className={styles.TabsLeft}>
              <ul>
                {volumeList && Object.keys(volumeList).map((option) => (
                  <li
                    key={option}
                    className={`${styles.Tab} ${getSelectTab(option)} ${tabInvalid(option) ? validateActive() : ''}`}
                    onClick={() => selectTab(option)}
                  >
                    {t(volumeList[option])}
                  </li>
                ))}
              </ul>
            </div>
            <div className={styles.TabsRight}>
              <div className={styles.OneColumns}>
                {getTabContent()}
              </div>
            </div>
          </div>
        )
        : (
          <div className={styles.AttachmentCollection}>
            <AttachmentCollection
              fileList={{ attachments: volumes?.[plantIndex]?.vehicleRestrictionFiles }}
              checkDisable={checkDisabled}
              download={(file) => downloadFile(file, file.toString().split('/').pop())}
              setDeleteFileId={setDeleteFileId}
              uploadFiles={uploadFiles}
              isAffectedVehicles
              uploadProgress={uploadProgress}
            />
          </div>
        )}
      {deleteFileId && (
        <ConfirmDialog
          close={() => setDeleteFileId(null)}
          confirm={() => deleteFile(deleteFileId)}
          title={t('dialog.confirmDelete')}
          message={t('dialog.wantToDelete')}
          closeBtnText={t('dialog.no')}
          confirmBtnText={t('dialog.yes')}
        />
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  volumes: getCampaignVehiclesVolumes(state),
  affectedVehicles: getCampaignVehicles(state),
  series: getCampaignVehicles(state)?.series || [],
  affectedVehiclesValidations: getAffectedVehiclesValidations(state),
  headerLanguage: getHeaderLanguage(state) || getCampaignInfo(state)?.defaultLanguage,
  validations: getCampaignValidations(state),
  volumeForms: getVolumesForms(state),
  limitationPlants: getLimitationPlants(state),
});

const actionCreators = {
  deleteAttachmentsPlant: deletePlantAttachments,
  addAttachmentsPlant: addPlantAttachments,
  setAffectedVehicles: setCampaignVehicles,
  setCopyAggreagate: copyAffectedAggregate,
  setCopyModelDesignation: copyAffectedVehiclesModelDesignation,
  setForms: setVolumesForms,
  setPlantsValidations: setAffectedVehiclesValidations,
};

export default connect(mapStateToProps, actionCreators)(VolumeDetails);
